import { useState, useEffect } from 'react'
import { MdInfoOutline, MdOutlineSearch } from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'

import type { ReactElement, KeyboardEvent, ChangeEvent } from 'react'

import ContentLayout from '~/components/ContentLayout'
import TextInput from '~/components/Inputs/Basic/TextInput'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import Tooltip from '~/components/Tooltip'
import UserInfoCard from '~/components/UserInfoCard'
import ViewTitle from '~/components/ViewTitle'
import { KEY_ENTER } from '~/const/shortcut'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useSearchUsersQuery } from '~/queries/app-users'
import { USER_TYPE } from '~/types/app-user'
import { debounce } from '~/utils/function'

import './styles.scss'

const Accounts = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const location = useLocation()

  const { dispatchToSearchParams, searchParamsObject } = useSearchParamsContext()

  const search = searchParamsObject.search || ''

  const [inputValue, setInputValue] = useState(search)

  /** Queries & Mutations */

  const { data: users, isError, isLoading } = useSearchUsersQuery(search)

  /** Effects */

  useDocumentTitle('Accounts')

  useEffect(() => {
    if (search) {
      setInputValue(search)
    }
  }, [search])

  /** Handlers */

  const handleSearch = debounce((event: KeyboardEvent<HTMLInputElement>) => {
    // @ts-expect-error value does not exist on Event target
    const searchValue = event.target.value

    if (event.key === KEY_ENTER && searchValue) {
      event.preventDefault()
      dispatchToSearchParams({ type: 'update', value: { search: searchValue } })
    }
  }, 500)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)

  const handleSeeDetails = (id: string, env: USER_TYPE) => () =>
    navigate(`/accounts/${id}/${env}`, { state: { from: `${location.pathname}${location.search}` } })

  return (
    <Layout className="apps-users-view">
      <ViewTitle>Accounts</ViewTitle>
      <ContentLayout>
        <div className="search-container">
          <div className="search-bar-wrapper">
            <TextInput
              autoFocus
              disabled={isLoading}
              icon={<MdOutlineSearch size={16} />}
              onChange={handleChange}
              onKeyDown={handleSearch}
              placeholder="Paste in an ID and press enter to validate"
              value={inputValue}
            />
          </div>
          <Tooltip
            align={{ points: ['bl', 'tl'] }}
            mouseEnterDelay={0.2}
            overlay={
              <div className="message">
                The ID can be a user ID, an installation ID, an email, a connection ID, or a provider ID.
              </div>
            }
            placement="topRight"
            trigger="hover"
          >
            <div className="search-info">
              <MdInfoOutline color="#aeaeae" size={20} />
            </div>
          </Tooltip>
        </div>
        {isLoading ? (
          <MwMLoader category="item" />
        ) : users ? (
          <div className="user-cards-container">
            {!users.prod_users && !users.dev_users ? (
              <div className="user-cards-container__no-users">No users found for this email address</div>
            ) : null}
            {users.prod_users
              ? users.prod_users.map((user) => (
                  <UserInfoCard
                    handleSeeMoreClick={handleSeeDetails(user.user_id, USER_TYPE.PROD)}
                    idSearched={search}
                    key={user.user_id}
                    user={user}
                    userType={USER_TYPE.PROD}
                  />
                ))
              : null}
            {users.dev_users
              ? users.dev_users.map((user) => (
                  <UserInfoCard
                    handleSeeMoreClick={handleSeeDetails(user.user_id, USER_TYPE.DEV)}
                    idSearched={search}
                    key={user.user_id}
                    user={user}
                    userType={USER_TYPE.DEV}
                  />
                ))
              : null}
          </div>
        ) : isError ? (
          <div className="content-container">
            <div className="no-items-mention">No items found</div>
          </div>
        ) : null}
      </ContentLayout>
    </Layout>
  )
}

export default Accounts
